<template>
  <div class="container-fluid py-4 px-5">
    <div class="text-center mb-4">
      <h1 class="title-page mb-1">Calendar & Events</h1>
      <p class="sub-title">Week {{ currentWeek }} of {{ currentYear }}</p>
    </div>

    <div class="row">
      <!-- Upcoming Events -->
      <div class="col-md-6 mb-4">
        <div class="card h-100 border-0 shadow-sm p-4 custom-rounded">
          <div class="d-flex align-items-center mb-4">
            <div class="icon-box blue-icon mr-2">
              <i class="far fa-calendar-alt"></i>
            </div>
            <h5 class="font-weight-bold mb-0">Upcoming Events</h5>
          </div>

          <div v-if="upcomingEvents.length === 0" class="text-center text-muted py-5">
            <i class="far fa-calendar-times fa-3x mb-3 opacity-3"></i>
            <p class="mb-0">No upcoming events</p>
          </div>

          <div
            v-else
            v-for="(event, index) in upcomingEvents"
            :key="index"
            class="event-item mb-3 p-2 d-flex align-items-start"
          >
            <div class="date-label font-weight-bold text-danger mr-3">
              {{ event.date }}
            </div>
            <div>
              <div class="font-weight-bold text-dark">
                {{ event.title }}
              </div>
              <p
                :class="['badge badge-pill mt-2', event.typeClass]"
              >
                {{ event.type }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Birthdays & Anniversaries -->
      <div class="col-md-6 mb-4">
        <div class="card h-100 border-0 shadow-sm p-4 custom-rounded">
          <div class="d-flex align-items-center mb-3">
            <i class="fas fa-birthday-cake text-pink mr-2"></i>
            <h5 class="font-weight-bold mb-0">Birthdays</h5>
          </div>

          <div v-if="birthdays.length === 0" class="text-center text-muted py-4">
            <i class="fas fa-birthday-cake fa-2x mb-3 opacity-3"></i>
            <p class="mb-0">No birthdays this week</p>
          </div>

          <div
            v-else
            v-for="(bday, index) in birthdays"
            :key="'b'+index"
            class="list-item-custom mb-3 p-3 d-flex justify-content-between align-items-center"
          >
            <p class="font-weight-bold">{{ bday.name }}</p>
            <p class="text-pink small font-weight-bold">{{ bday.day }}</p>
          </div>

          <div class="d-flex align-items-center mt-4 mb-3">
            <i class="far fa-heart text-danger mr-2"></i>
            <h5 class="font-weight-bold mb-0">Work Anniversaries</h5>
          </div>

          <div v-if="anniversaries.length === 0" class="text-center text-muted py-4">
            <i class="far fa-heart fa-2x mb-3 opacity-3"></i>
            <p class="mb-0">No work anniversaries</p>
          </div>

          <div
            v-else
            v-for="(anniv, index) in anniversaries"
            :key="'a'+index"
            class="list-item-custom p-3 d-flex justify-content-between align-items-center"
          >
            <div>
              <div class="font-weight-bold">{{ anniv.name }}</div>
              <div class="">
                {{ anniv.years }} years with ADITI
              </div>
            </div>

            <span class="text-danger small font-weight-bold">
              {{ anniv.day }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Countdown -->
    <div class="row">
      <div class="col-12">
        <div
          class="countdown-bar d-flex align-items-center justify-content-between px-4 text-white shadow-sm"
        >
          <div class="d-flex align-items-center">
            <i class="far fa-calendar-check fa-2x mr-3"></i>
            <div>
              <div class=" opacity-7 mt-2 mb-2">Countdown</div>
              <h5 class="font-weight-bold mb-0">
                {{ countdown.days }} days until {{ countdown.title }}
              </h5>
            </div>
          </div>
          <div class="display-3 font-weight-bold opacity-3">{{ countdown.days }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import {axiosGet} from "../../../common/Helper/AxiosHelper";
import {PUBLIC_DASHBOARD} from "../../../tenant/Config/ApiUrl";

export default {
  computed: {
    currentWeek() {
      return moment().isoWeek();
    },
    currentYear() {
      return moment().year();
    }
  },
  data() {
    return {
      upcomingEvents: [],
      birthdays: [],
      anniversaries: [],
      countdown: {
        title: 'Loading...',
        days: 0
      },
      timerInterval: null
    }
  },
  methods: {
    getData() {
      axiosGet(`${PUBLIC_DASHBOARD}/calendar-events`).then(({data}) => {
        this.upcomingEvents = data.upcomingEvents;
        this.birthdays = data.birthdays;
        this.anniversaries = data.anniversaries;
        if (data.countdown) {
          this.countdown = data.countdown;
        }
      });
    }
  },
  created() {
    this.getData();
  },
  mounted() {
    this.timerInterval = setInterval(this.getData, 60 * 1000); // Update every minute
  },
  beforeUnmount() {
    clearInterval(this.timerInterval); // Clear the interval when the component is unmounted
  }
}
</script>

<style scoped>
/* Base */
.container-fluid {
  overflow: hidden; /* Remove scroll */
}

/* New Styles */
.title-page {
    font-size: 1.6rem; /* text-5xl */
    line-height: 1; 
    font-weight: 1000; /* font-bold */
    color: rgb(17, 24, 39); /* text-gray-900 */
    letter-spacing: -0.025em; /* tracking-tight */
} 
.sub-title {
    font-size: 1rem; /* text-2xl (24px) */
    line-height: 2rem; 
    color: rgb(107, 114, 128); /* text-gray-500 */
}
.section-title {
    font-size: 1.6rem;                                                                                                                                
    line-height: 2rem; 
    font-weight: 700; 
}

h1.font-weight-bold { font-size: 2.5rem; }
p.text-muted { font-size: 1.2rem; }
h5 { font-size: 1.2rem; }

.custom-rounded { border-radius: 10px;   box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
}
.text-pink { color: #e83e8c; }
.blue-icon { color: #007bff; font-size: 1.5rem; }
.opacity-7 { opacity: 0.7; font-size: 1.1rem; }
.opacity-3 { opacity: 0.3; }
.smallest { font-size: 0.9rem; }

.event-item { background-color: #f8f2f2; border-radius: 5px; font-size: 1.1rem;   box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
}
.list-item-custom { background-color: #fdf2f4; border-radius: 5px; font-size: 1.1rem; }
.date-label { width: 60px; font-size: 1.1rem; }

.badge-holiday {
  background-color: #e7f0ff;
  color: #007bff;
  /* font-size: 0.65rem; */
}

.badge-event {
  background-color: #e6f9f1;
  color: #28a745;
  /* font-size: 0.65rem; */
}

.countdown-bar {
  background: linear-gradient(90deg, #1e60ff 0%, #2a69ff 100%);
  height: 102px;
  border-radius: 10px;
}

.shadow-sm {
  box-shadow: 0 .125rem .25rem rgba(0,0,0,.04)!important;
}

/* ===================== */
/* RESPONSIVE BREAKPOINTS */
/* ===================== */

/* ≤1200px */
@media (max-width: 1200px) {
  .container-fluid {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

/* ≤992px */
@media (max-width: 992px) {
  .countdown-bar {
    height: auto;
    padding: 1.5rem;
  }

  .display-3 {
    font-size: 3.5rem;
  }
}

/* ≤768px */
@media (max-width: 768px) {
  .container-fluid {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .countdown-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .countdown-bar .display-3 {
    margin-top: 1rem;
  }

  h5 {
    font-size: 1rem;
  }
}

/* ≤576px */
@media (max-width: 576px) {
  .container-fluid {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .event-item {
    flex-direction: column;
  }

  .date-label {
    width: auto;
    margin-bottom: 0.25rem;
  }

  .countdown-bar i {
    font-size: 1.8rem;
  }

  .display-3 {
    font-size: 2.5rem;
  }
}
</style>
